<template>
	<div
		class="mask"
		@click.self="$emit('close')"
	>
		<div class="panel">
			<!-- ───── header ───── -->
			<header>
				<h2>
					Condividi device con <span class="user-email">{{ user?.email }}</span>
				</h2>
				<button
					class="x"
					@click="$emit('close')"
				>
					×
				</button>
			</header>

			<!-- ───── elenco device ───── -->
			<div
				v-if="!loading"
				class="list-wrap"
			>
				<p
					v-if="!devices.length"
					class="msg"
				>
					Nessun device disponibile.
				</p>
				<ul v-else>
					<li
						v-for="d in devices"
						:key="d.id"
					>
						<div class="device-row-container">
							<label class="device-row">
								<input
									type="checkbox"
									:value="d.id"
									v-model="selectedDeviceIds"
									@change="ensurePermissionsInit(d.id)"
								/>
								<div class="device-info">
									<strong>#{{ d.id }}</strong>
									<span class="device-name">{{ d.name || "— senza nome —" }}</span>
									<span
										v-if="d.is_archived"
										class="badge-archived"
										>Archiviato</span
									>
								</div>
							</label>

							<!-- Permissions sub-section -->
							<div
								v-if="selectedDeviceIds.includes(d.id)"
								class="permissions-box"
							>
								<label class="perm-label">
									<input
										type="checkbox"
										v-model="permissionsMap[d.id].can_edit_status"
									/>
									<span>Modifica Stato</span>
								</label>
								<label class="perm-label">
									<input
										type="checkbox"
										v-model="permissionsMap[d.id].can_edit_thresholds"
									/>
									<span>Modifica Soglie</span>
								</label>
							</div>
						</div>
					</li>
				</ul>
			</div>

			<p
				v-else
				class="loading"
			>
				Caricamento devices…
			</p>

			<p
				v-if="error"
				class="error"
			>
				{{ error }}
			</p>

			<!-- ───── footer actions ───── -->
			<footer>
				<button
					class="btn-cancel"
					@click="$emit('close')"
				>
					Annulla
				</button>
				<button
					class="btn-save"
					:disabled="saving || loading"
					@click="saveChanges"
				>
					{{ saving ? "Salvataggio..." : "Salva modifiche" }}
				</button>
			</footer>
		</div>
	</div>
</template>

<script setup lang="ts">
	import axios from "@/axiosInstance";
	import { onMounted, ref } from "vue";
	import { useToast } from "vue-toast-notification";

	const props = defineProps<{
		user: any;
	}>();

	const emit = defineEmits(["close", "saved"]);
	const toast = useToast();

	const devices = ref<any[]>([]);
	const selectedDeviceIds = ref<number[]>([]);
	const permissionsMap = ref<Record<number, { can_edit_status: boolean; can_edit_thresholds: boolean }>>({});

	const loading = ref(false);
	const saving = ref(false);
	const error = ref<string | null>(null);

	/* ─── Caricamento dati ─── */
	onMounted(loadData);

	async function loadData() {
		if (!props.user?.id) return;

		loading.value = true;
		error.value = null;

		try {
			// 1. Carico TUTTI i device
			const { data: allDevices } = await axios.get("/devices/get-all-graphs", { withCredentials: true });
			devices.value = allDevices || [];

			// Initialize permissions map for all devices with defaults
			devices.value.forEach((d) => {
				if (!permissionsMap.value[d.id]) {
					permissionsMap.value[d.id] = { can_edit_status: false, can_edit_thresholds: false };
				}
			});

			// 2. Carico i device GIA' condivisi con questo utente
			const { data: shared } = await axios.get(`/auth/${props.user.id}/shared-devices`, { withCredentials: true });

			const sharedList = Array.isArray(shared) ? shared : shared?.devices || [];

			const ids: number[] = [];
			sharedList.forEach((item: any) => {
				const id = typeof item === "object" ? item.id : item;
				ids.push(id);

				// If we have permissions data, populate it
				if (typeof item === "object" && item.permissions) {
					permissionsMap.value[id] = {
						can_edit_status: !!item.permissions.can_edit_status,
						can_edit_thresholds: !!item.permissions.can_edit_thresholds,
					};
				}
			});
			selectedDeviceIds.value = ids;
		} catch (e: any) {
			console.error(e);
			error.value = e.response?.data?.details || "Errore durante il caricamento";
		} finally {
			loading.value = false;
		}
	}

	function ensurePermissionsInit(deviceId: number) {
		if (!permissionsMap.value[deviceId]) {
			permissionsMap.value[deviceId] = { can_edit_status: false, can_edit_thresholds: false };
		}
	}

	/* ─── Salvataggio ─── */
	async function saveChanges() {
		saving.value = true;
		try {
			// Construct the new payload format: { devices: [{ id, permissions: {...} }, ...] }
			const devicesPayload = selectedDeviceIds.value.map((id) => {
				return {
					id,
					permissions: permissionsMap.value[id] || { can_edit_status: false, can_edit_thresholds: false },
				};
			});

			await axios.post(
				`/auth/${props.user.id}/share-devices`,
				{
					devices: devicesPayload,
				},
				{ withCredentials: true }
			);

			toast.success("Condivisioni aggiornate con successo", {
				position: "top-right",
				duration: 2500,
			});
			emit("saved");
			emit("close");
		} catch (e: any) {
			toast.error(e.response?.data?.details || "Errore nel salvataggio", {
				position: "top-right",
				duration: 2500,
			});
		} finally {
			saving.value = false;
		}
	}
</script>

<style scoped>
	.mask {
		position: fixed;
		inset: 0;
		backdrop-filter: blur(3px);
		background: rgba(0, 0, 0, 0.55);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 9999;
	}
	.panel {
		width: 450px;
		max-width: 90%;
		background: #0d0d0d;
		color: #fff;
		padding: 24px;
		border-radius: 8px;
		box-shadow: 0 18px 40px rgba(0, 0, 0, 0.4);
		display: flex;
		flex-direction: column;
		gap: 18px;
		max-height: 80vh;
	}
	header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		gap: 10px;
	}
	h2 {
		font: 600 18px/1.3 "Inter", sans-serif;
		margin: 0;
		color: #fff;
	}
	.user-email {
		color: #008f8f;
		font-weight: 700;
	}

	.x {
		background: none;
		border: 0;
		color: #fff;
		font-size: 24px;
		line-height: 1;
		cursor: pointer;
		padding: 0;
	}

	.list-wrap {
		flex: 1;
		overflow-y: auto;
		min-height: 150px;
		border: 1px solid #282828;
		border-radius: 6px;
		background: #111;
		padding: 6px;
	}

	ul {
		list-style: none;
		margin: 0;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.device-row-container {
		display: flex;
		flex-direction: column;
		background: transparent;
		border-radius: 4px;
		transition: background 0.15s;
	}
	.device-row-container:hover {
		background: #1f1f1f;
	}

	.device-row {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 8px 10px;
		cursor: pointer;
	}

	.device-row input[type="checkbox"] {
		width: 16px;
		height: 16px;
		accent-color: #008f8f;
		cursor: pointer;
	}
	.device-info {
		display: flex;
		flex-direction: column;
		line-height: 1.3;
	}
	.device-info strong {
		color: #888;
		font-size: 11px;
	}
	.device-name {
		font-size: 14px;
		color: #eee;
		font-weight: 500;
	}
	.badge-archived {
		display: inline-block;
		font-size: 10px;
		background: #333;
		color: #fa5;
		padding: 1px 4px;
		border-radius: 3px;
		margin-top: 2px;
		align-self: flex-start;
	}

	/* Permissions sub-section */
	.permissions-box {
		display: flex;
		gap: 16px;
		padding: 0 10px 10px 38px; /* Indent to align with text */
	}
	.perm-label {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 12px;
		color: #bbb;
		cursor: pointer;
		user-select: none;
	}
	.perm-label input[type="checkbox"] {
		width: 14px;
		height: 14px;
		accent-color: #008f8f;
	}
	.perm-label:hover span {
		color: #fff;
	}

	.loading,
	.msg {
		color: #bbb;
		text-align: center;
		margin-top: 20px;
	}
	.error {
		color: #e74c3c;
		font-size: 14px;
	}

	footer {
		display: flex;
		justify-content: flex-end;
		gap: 12px;
		padding-top: 10px;
		border-top: 1px solid #222;
	}

	button.btn-save,
	button.btn-cancel {
		padding: 8px 16px;
		border-radius: 4px;
		font: 600 14px/1 "Inter", sans-serif;
		cursor: pointer;
		border: 0;
	}
	.btn-cancel {
		background: transparent;
		color: #aaa;
		border: 1px solid #444;
	}
	.btn-cancel:hover {
		color: #fff;
		border-color: #666;
	}

	.btn-save {
		background: #008f8f;
		color: #fff;
	}
	.btn-save:hover:not(:disabled) {
		background: #00a7a7;
	}
	.btn-save:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
</style>
